import { log } from '@/utils/logger'
import { resolveNetworkOnLoad } from './networkPreference'
export type NetworkType = 'testnet' | 'mainnet'

export interface NetworkConfig {
  name: string
  displayName: string
  description: string
  rpcUrl: string
  rpcApiKey?: string // API key for RPC pool (unused, kept for interface compat)
  wsUrl?: string
  status: 'active' | 'coming-soon' | 'deprecated'
  api: {
    url: string
    port: number
  }
  database: {
    host: string
    port: number
    database: string
    schema: string
    user: string
    password: string
    ssl: boolean
    poolSize: number
  }
  geyser: {
    endpoint: string
    apiKey: string
    enabled: boolean
    rpcEndpoint?: string // Optional direct RPC endpoint (bypasses rate limits)
  }
  performance: {
    theoreticalMaxTPS: number
    expectedTPS: number
    historicalMaxTPS: number // Actual recorded maximum TPS achieved on this network
  }
}

export interface AppConfig {
  app: {
    name: string
    version: string
    refreshInterval: number
    port: {
      primary: number
      rangeStart: number
      rangeEnd: number
    }
  }
  networks: {
    testnet: NetworkConfig
    mainnet: NetworkConfig
  }
  defaultNetwork: NetworkType
}

// Environment-based configuration
const config: AppConfig = {
  app: {
    name: import.meta.env.VITE_APP_NAME || 'X1 Blockchain Explorer',
    version: import.meta.env.VITE_APP_VERSION || '1.0.0',
    refreshInterval: parseInt(import.meta.env.VITE_REFRESH_INTERVAL || '10000'),
    port: {
      primary: parseInt(import.meta.env.VITE_PRIMARY_PORT || '9309'),
      rangeStart: parseInt(import.meta.env.VITE_PORT_RANGE_START || '9309'),
      rangeEnd: parseInt(import.meta.env.VITE_PORT_RANGE_END || '9319'),
    },
  },
  networks: {
    testnet: {
      name: 'testnet',
      displayName: 'Testnet',
      description: 'Public testnet for development and testing',
      rpcUrl: import.meta.env.VITE_TESTNET_RPC_URL || 'https://rpc.testnet.x1.xyz',
      wsUrl: import.meta.env.VITE_TESTNET_WS_URL || 'wss://rpc.testnet.x1.xyz',
      status: 'active',
      api: {
        // Use relative URLs for API calls - works everywhere (localhost, VPS, remote machines)
        // Vite dev proxy and Nginx will both route /api to the backend automatically
        url: import.meta.env.VITE_API_URL || '',
        port: parseInt(import.meta.env.VITE_API_PORT || '8547')
      },
      database: {
        // Wave D supabase cleanup [WP #3598, 2026-05-03, release-director]:
        //   Removed literal supabase[.]falcon-python[.]ts[.]net + literal reader
        //   credential fallbacks. Frontend code paths that talked directly to
        //   the cluster DB are gated by `typeof window === 'undefined'` and
        //   never execute in-browser; the server-side path uses env-only
        //   credentials via /etc/axio. Empty-string fallback here makes a
        //   misconfigured build fail fast at first query rather than silently
        //   pointing at a sunset host.
        host: import.meta.env.VITE_TESTNET_DB_HOST || '',
        port: parseInt(import.meta.env.VITE_TESTNET_DB_PORT || '5432'),
        database: import.meta.env.VITE_TESTNET_DB_NAME || 'postgres',
        schema: import.meta.env.VITE_TESTNET_DB_SCHEMA || 'fortiblox_explorer_testnet',
        user: import.meta.env.VITE_TESTNET_DB_USER || '',
        password: import.meta.env.VITE_TESTNET_DB_PASSWORD || '',
        ssl: import.meta.env.VITE_TESTNET_DB_SSL === 'true',
        poolSize: parseInt(import.meta.env.VITE_TESTNET_DB_POOL_SIZE || '10'),
      },
      geyser: {
        endpoint: import.meta.env.VITE_TESTNET_GEYSER_ENDPOINT || 'https://rpc.testnet.x1.xyz:10000',
        apiKey: import.meta.env.VITE_TESTNET_GEYSER_API_KEY || '',
        enabled: import.meta.env.VITE_TESTNET_GEYSER_ENABLED === 'true',
        rpcEndpoint: import.meta.env.VITE_TESTNET_GEYSER_RPC || 'https://rpc.testnet.x1.xyz'
      },
      performance: {
        theoreticalMaxTPS: parseInt(import.meta.env.VITE_TESTNET_THEORETICAL_MAX_TPS || '65000'),
        expectedTPS: parseInt(import.meta.env.VITE_TESTNET_EXPECTED_TPS || '1500'),
        historicalMaxTPS: parseInt(import.meta.env.VITE_TESTNET_HISTORICAL_MAX_TPS || '15721') // Actual max recorded on testnet
      },
    },
    mainnet: {
      name: 'mainnet',
      displayName: 'Mainnet',
      description: 'Production mainnet environment',
      rpcUrl: import.meta.env.VITE_MAINNET_RPC_URL || 'https://rpc.mainnet.x1.xyz',
      wsUrl: import.meta.env.VITE_MAINNET_WS_URL || 'wss://rpc.mainnet.x1.xyz/',
      status: 'active',
      api: {
        // Use relative URLs for API calls - works everywhere (localhost, VPS, remote machines)
        // Vite dev proxy and Nginx will both route /api to the backend automatically
        url: import.meta.env.VITE_API_URL || '',
        port: parseInt(import.meta.env.VITE_API_PORT || '8547')
      },
      database: {
        // Wave D supabase cleanup [WP #3598, 2026-05-03, release-director]:
        //   Same rationale as testnet block above. Note: prior fallback also
        //   leaked a TESTNET reader credential into the mainnet block — a
        //   pre-existing copy-paste defect surfaced during this cleanup.
        host: import.meta.env.VITE_MAINNET_DB_HOST || '',
        port: parseInt(import.meta.env.VITE_MAINNET_DB_PORT || '5432'),
        database: import.meta.env.VITE_MAINNET_DB_NAME || 'postgres',
        schema: import.meta.env.VITE_MAINNET_DB_SCHEMA || 'fortiblox_explorer_mainnet',
        user: import.meta.env.VITE_MAINNET_DB_USER || '',
        password: import.meta.env.VITE_MAINNET_DB_PASSWORD || '',
        ssl: import.meta.env.VITE_MAINNET_DB_SSL === 'true',
        poolSize: parseInt(import.meta.env.VITE_MAINNET_DB_POOL_SIZE || '10'),
      },
      geyser: {
        endpoint: import.meta.env.VITE_MAINNET_GEYSER_ENDPOINT || '100.88.174.39:10000',
        apiKey: import.meta.env.VITE_MAINNET_GEYSER_API_KEY || 'x1-geyser-shared-2025-d1f1cfb97ab6b4f7d0d07b173701ac7a57e2c1a3a812fb4a9f09c76c63466a95',
        enabled: import.meta.env.VITE_MAINNET_GEYSER_ENABLED === 'true' || true,
        rpcEndpoint: import.meta.env.VITE_MAINNET_GEYSER_RPC || 'http://100.88.174.39:8899'
      },
      performance: {
        theoreticalMaxTPS: parseInt(import.meta.env.VITE_MAINNET_THEORETICAL_MAX_TPS || '100000'), // Higher for production mainnet
        expectedTPS: parseInt(import.meta.env.VITE_MAINNET_EXPECTED_TPS || '5000'),
        historicalMaxTPS: parseInt(import.meta.env.VITE_MAINNET_HISTORICAL_MAX_TPS || '15271') // Real recorded mainnet all-time peak (pre-ClickHouse migration). The live tps_per_minute table only retains ~30d so it can't see it; floor at the verified historical max. Env can still override.
      },
    },
  },
  defaultNetwork: (import.meta.env.VITE_DEFAULT_NETWORK as NetworkType) || 'mainnet',
}

// Configuration service with network switching
class ConfigService {
  private static instance: ConfigService
  private currentNetwork: NetworkType

  private constructor() {
    this.currentNetwork = this.loadNetworkPreference()
  }

  public static getInstance(): ConfigService {
    if (!ConfigService.instance) {
      ConfigService.instance = new ConfigService()
    }
    return ConfigService.instance
  }

  public getCurrentNetwork(): NetworkType {
    return this.currentNetwork
  }

  public setCurrentNetwork(network: NetworkType): void {
    const previousNetwork = this.currentNetwork
    this.currentNetwork = network
    this.saveNetworkPreference(network)

    // If network actually changed, invalidate cache and emit event
    if (previousNetwork !== network) {
      // Directly reinitialize explorerService
      try {
        // Import explorerService dynamically to avoid circular imports
        import('@/services/explorerService').then((module) => {
          module.default.reinitializeForNetwork(network)
        })
      } catch (error) {
        log.error('ConfigService: Error reinitializing explorerService:', error);
        log.warn('Could not reinitialize explorerService:', error)
      }

      // Emit custom event for NetworkSwitchManager to handle cache invalidation
      // The NetworkSwitchManager (initialized in main.jsx) will handle all cache invalidation
      window.dispatchEvent(new CustomEvent('networkChanged', {
        detail: { previousNetwork, currentNetwork: network }
      }))

      log.info(`Network switched: ${previousNetwork} -> ${network}`)
    }
  }

  public getCurrentNetworkConfig(): NetworkConfig {
    return config.networks[this.currentNetwork]
  }

  public getNetworkConfig(network: NetworkType): NetworkConfig {
    return config.networks[network]
  }

  public isNetworkAvailable(network: NetworkType): boolean {
    const networkConfig = config.networks[network]
    return networkConfig.status === 'active' && !networkConfig.database.host.includes('PLACEHOLDER')
  }

  public isMainnetAvailable(): boolean {
    return this.isNetworkAvailable('mainnet')
  }

  public getAvailableNetworks(): NetworkConfig[] {
    return Object.values(config.networks).filter(network => network.status === 'active')
  }

  public getNetworksByStatus(status: 'active' | 'coming-soon' | 'deprecated'): NetworkConfig[] {
    return Object.values(config.networks).filter(network => network.status === status)
  }

  public getAppConfig() {
    return config.app
  }

  public getAllNetworks(): NetworkConfig[] {
    return Object.values(config.networks)
  }

  private loadNetworkPreference(): NetworkType {
    // Delegate the (migration + preference) decision to the pure, tested helper.
    // The visible network selector was removed (2026-08) and the explorer
    // defaults everyone to mainnet via a one-time reset; a deliberate hidden-"t"
    // toggle can still persist testnet. See resolveNetworkOnLoad().
    try {
      const resolved = resolveNetworkOnLoad(
        {
          get: (k) => localStorage.getItem(k),
          set: (k, v) => localStorage.setItem(k, v),
          remove: (k) => localStorage.removeItem(k),
        },
        config.defaultNetwork,
        (n) => this.isNetworkAvailable(n),
      )
      log.info(`📡 Resolved network on load: ${resolved}`)
      // If the resolved network is genuinely available, use it.
      if (this.isNetworkAvailable(resolved)) return resolved
    } catch (error) {
      log.warn('Failed to resolve network preference:', error)
    }

    const defaultNetwork = config.defaultNetwork
    if (this.isNetworkAvailable(defaultNetwork)) {
      return defaultNetwork
    }

    // Fallback: if configured default is not available, use first available network
    const availableNetworks = this.getAvailableNetworks()
    const fallbackNetwork = availableNetworks.length > 0 ? availableNetworks[0].name as NetworkType : 'testnet'
    log.info(`⚠️ Configured default network "${defaultNetwork}" not available, using fallback: ${fallbackNetwork}`)

    try {
      localStorage.setItem('x1-explorer-network', fallbackNetwork)
    } catch (error) {
      log.warn('Failed to save fallback network to localStorage:', error)
    }

    return fallbackNetwork
  }

  private saveNetworkPreference(network: NetworkType): void {
    try {
      localStorage.setItem('x1-explorer-network', network)
    } catch (error) {
      log.warn('Failed to save network preference:', error)
    }
  }
}

export const configService = ConfigService.getInstance()
export default config
